import { useState } from 'react';
import {
  alpha,
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useRecoilValue } from 'recoil';
import { hasInternalDashAccessState } from 'states';
import { MoviefoneAnalysis, SearchCandidate } from 'gen/stream-api/requests';
import { StrategyOptionsCalculator } from './MoviefoneStrategyCard';
import { LegsSummary, TickerQuote } from './MoviefoneShared';

type ChartMode = 'chart' | 'heatmap';

interface MoviefoneComparePanelProps {
  searchCandidate: SearchCandidate;
  symbol: string;
  chartMode: ChartMode;
}

const MoviefoneComparePanel = ({
  searchCandidate,
  symbol,
  chartMode,
}: MoviefoneComparePanelProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
        pt: '4px',
        pb: '12px',
        px: '12px',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {searchCandidate.family_label}
      </Typography>
      <Box sx={{ mb: 1 }}>
        <LegsSummary legs={searchCandidate.legs} variant="caption" />
      </Box>
      <StrategyOptionsCalculator
        searchCandidate={searchCandidate}
        symbol={symbol}
        hidePnlChart={chartMode === 'heatmap'}
        hideHeatmap={chartMode === 'chart'}
        simplifedPositions={false}
        positionsBottomMargin={0}
        defaultPositionsCollapsed
      />
    </Box>
  );
};

interface MoviefoneCompareViewProps {
  moviefoneAnalysis: MoviefoneAnalysis;
  strategies: SearchCandidate[];
}

// Lets a user look at all recommended trades' P&L side by side, rather than
// clicking into each one individually and losing the others from view.
const MoviefoneCompareView = ({
  moviefoneAnalysis,
  strategies,
}: MoviefoneCompareViewProps) => {
  const theme = useTheme();
  const [chartMode, setChartMode] = useState<ChartMode>('chart');
  const isInternalUser = useRecoilValue(hasInternalDashAccessState);

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          p: '12px',
          gap: 2,
          flexWrap: 'wrap',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          <TickerQuote
            symbol={moviefoneAnalysis.symbol}
            spot={moviefoneAnalysis.spot}
            tradeDate={moviefoneAnalysis.trade_date}
          />
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={chartMode}
          onChange={(_, value: ChartMode | null) =>
            value != null && setChartMode(value)
          }
          aria-label="Chart mode"
        >
          <ToggleButton
            value="chart"
            aria-label="PnL Chart"
            sx={{
              fontSize: '11px',
              textTransform: 'none',
              py: '2px',
              px: '8px',
              border: `1px solid ${alpha(theme.palette.text.secondary, 0.3)}`,
              color: theme.palette.primary.main,
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.16),
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            PnL Chart
          </ToggleButton>
          {isInternalUser && (
            <ToggleButton
              value="heatmap"
              aria-label="Heatmap"
              sx={{
                fontSize: '11px',
                textTransform: 'none',
                py: '2px',
                px: '8px',
                border: `1px solid ${alpha(theme.palette.text.secondary, 0.3)}`,
                color: theme.palette.primary.main,
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.16),
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              Heatmap
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '12px' }}>
        <Stack spacing={2}>
          {strategies.map((searchCandidate, i) => (
            <MoviefoneComparePanel
              key={i}
              searchCandidate={searchCandidate}
              symbol={moviefoneAnalysis.symbol}
              chartMode={chartMode}
            />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

export default MoviefoneCompareView;
